/**
 * Error message sanitization utility.
 * Prevents exposure of internal paths, table names, or stack traces.
 * @see docs/specifications/2026-01-20-manual-source-phase3-frontend.md Section 6.3
 */

import { TURNSTILE_ERROR_MESSAGE } from '../constants/turnstile';

/**
 * Mapping of known error codes to user-friendly Polish messages.
 */
const ERROR_CODE_MESSAGES: Record<string, string> = {
  INVALID_URL: 'URL nie może wskazywać na localhost ani prywatne adresy IP.',
  SOURCE_NOT_MANUAL: 'To źródło nie obsługuje ręcznego dodawania artykułów.',
  SOURCE_INACTIVE: 'Źródło jest nieaktywne.',
  SOURCE_NOT_FOUND: 'Źródło nie istnieje.',
  ARTICLE_ALREADY_LINKED: 'Artykuł o tym URL już istnieje w tym źródle.',
  UNAUTHORIZED: 'Brak uprawnień do wykonania tej operacji.',
  FORBIDDEN: 'Dostęp zabroniony.',
  TURNSTILE_INVALID: TURNSTILE_ERROR_MESSAGE,
  TURNSTILE_COOKIE_MISSING: TURNSTILE_ERROR_MESSAGE,
  TURNSTILE_COOKIE_EXPIRED: TURNSTILE_ERROR_MESSAGE,
  TOO_MANY_REQUESTS: 'Zbyt wiele żądań. Spróbuj ponownie za chwilę.',
  INTERNAL_ERROR: 'Wystąpił błąd serwera. Spróbuj ponownie później.',
};

const GENERIC_ERROR = 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.';

export interface ApiErrorLike {
  code?: string;
  message?: string;
  status?: number;
  statusCode?: number;
  response?: {
    status?: number;
    data?: {
      code?: string;
      errorCode?: string;
      message?: string;
      statusCode?: number;
    };
  };
}

/**
 * Sanitizes an API error to a user-friendly message.
 * Never exposes raw backend messages - uses mapped or generic messages.
 * @param error - The error object from API call
 * @returns User-friendly error message in Polish
 */
export function sanitizeErrorMessage(error: ApiErrorLike): string {
  // Extract error code from various error shapes (backend uses both 'code' and 'errorCode')
  const code = error.code || error.response?.data?.errorCode || error.response?.data?.code;
  const status = error.status || error.statusCode || error.response?.status || error.response?.data?.statusCode;

  // 1. If known error code, use mapped message
  if (code && ERROR_CODE_MESSAGES[code]) {
    return ERROR_CODE_MESSAGES[code];
  }

  // 2. If HTTP status code, use generic message
  if (status && status >= 500) {
    return ERROR_CODE_MESSAGES.INTERNAL_ERROR;
  }

  if (status === 429) {
    return ERROR_CODE_MESSAGES.TOO_MANY_REQUESTS;
  }

  if (status === 401) {
    return ERROR_CODE_MESSAGES.UNAUTHORIZED;
  }

  if (status === 403) {
    return ERROR_CODE_MESSAGES.FORBIDDEN;
  }

  // 3. Never expose raw backend message - use generic
  return GENERIC_ERROR;
}

/**
 * Extracts error code from various error shapes.
 * Useful for conditional rendering based on error type.
 * @param error - The error object from API call
 * @returns Error code string or undefined
 */
export function extractErrorCode(error: ApiErrorLike): string | undefined {
  return error.code || error.response?.data?.errorCode || error.response?.data?.code;
}

/**
 * Extracts existing article ID from duplicate error.
 * Used to provide "view existing" link in duplicate modal.
 * @param error - The error object from API call
 * @returns Existing article ID or undefined
 */
export function extractExistingArticleId(error: ApiErrorLike & { existingArticleId?: string; response?: { data?: { existingArticleId?: string } } }): string | undefined {
  return error.existingArticleId || error.response?.data?.existingArticleId;
}
